/**
 * TasksTab — the Action Center's user-progression surface (DEV-681 Phase 3).
 *
 * Renders the user's outstanding milestones as a worklist:
 *   - Sorted by severity (mandatory → recommended → optional)
 *   - With CTA buttons that route to the surface where the user can
 *     complete the action
 *   - "Show completed" toggle exposes already-achieved milestones
 *   - Empty state when the archetype's progression is fully done
 *
 * Path C runtime (per Skip's locked decision): the tab uses a synthetic
 * client-side evaluator (`milestoneEvaluator.ts`) that derives the
 * achieved-milestones set from existing user state — no backend
 * MilestoneEvaluator service required for v1. When DEV-508 ships its
 * proper server-side evaluator, the inputs to this component change but
 * the rendering doesn't.
 *
 * Archetype resolution: defaults to `explorer` if the user's archetype
 * isn't yet stored. This matches the stub state until DEV-500's runtime
 * pipeline (Phase 3 of that epic) is wired. `getArchetypeConfig()`
 * handles legacy slug migration transparently.
 *
 * Locked design decisions in this MVP:
 *   - System-detected completion only (no user "mark done" button).
 *   - Severity tiering: mandatory · recommended · optional.
 *   - Tier-gating with locked-but-visible affordance — DEFERRED to
 *     follow-up; the four-tier model in DEV-725 isn't wired to
 *     archetypes yet.
 *   - OR-groups (e.g., "Topic Tracker OR Project Chronicle") — DEFERRED
 *     to follow-up; needs a schema extension on MilestoneUnlock.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  getArchetypeConfig,
  listIncompleteMilestones,
  listCompletedMilestones,
  type MilestoneSeverity,
  type MilestoneUnlock,
} from '../../Data/Onboarding/archetypeConfig';
import { evaluateMilestones } from '../../Data/Onboarding/milestoneEvaluator';
import EulaModal from '../../Components/EulaModal/EulaModal';
import TotpSetupModal from '../../Components/TotpSetup/TotpSetupModal';
import styles from './TasksTab.module.css';

// ── Types ───────────────────────────────────────────────────

export interface TasksTabProps {
  currentUserGuid: string;
  /** All security users — used to find the current user's profile state. */
  users?: Record<string, any>[];
  /** All community memberships from useApiSecurityData. */
  communityMemberships?: Record<string, any>[];
  /** All project ACL rows from useApiSecurityData. */
  projectAcl?: Record<string, any>[];
  /** All projects (from useApiProjects). */
  projects?: Record<string, any>[];
  /**
   * Optional: the user's archetype slug, when known. Defaults to
   * 'explorer' for users without a stored archetype yet. Legacy slugs
   * (e.g. 'expert', 'system_admin') are migrated transparently by
   * `getArchetypeConfig()`.
   */
  archetypeSlug?: string;
}

// ── Helpers ─────────────────────────────────────────────────

/** Localized label for a severity tier. Used for visual pills + sr-only. */
function useSeverityLabel(): (sev: MilestoneSeverity) => string {
  const { t } = useAppTranslation('pages');
  return (sev) => t(`actionCenter.tasks.severity.${sev}`);
}

// ── Component ───────────────────────────────────────────────

const TasksTab: React.FC<TasksTabProps> = ({
  currentUserGuid,
  users,
  communityMemberships,
  projectAcl,
  projects,
  archetypeSlug,
}) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const severityLabel = useSeverityLabel();

  const [showCompleted, setShowCompleted] = useState(false);
  // DEV-906: certain milestones use an in-page modal rather than route
  // navigation. accept_eula opens EulaModal so users don't have to leave
  // the Tasks tab. The detector picks up the new state on save and the
  // milestone falls off the outstanding list automatically.
  const [eulaOpen, setEulaOpen] = useState(false);

  // Resolve the user's row from the security users array.
  const currentUser = useMemo(() => {
    const guid = (currentUserGuid || '').toUpperCase();
    if (!guid) return null;
    for (const u of users || []) {
      if (String(u?.user_guid || '').toUpperCase() === guid) return u;
    }
    return null;
  }, [users, currentUserGuid]);

  // Resolve the archetype config (with legacy-slug migration).
  const archetypeConfig = useMemo(
    () => getArchetypeConfig(archetypeSlug || 'explorer'),
    [archetypeSlug],
  );

  // Run the synthetic evaluator to derive the achieved-milestones set.
  // This is the entire Path C runtime — pure function, no server roundtrip.
  const achievedMilestones = useMemo(
    () =>
      evaluateMilestones({
        currentUserGuid,
        currentUser,
        communityMemberships,
        projectAcl,
        projects,
      }),
    [currentUserGuid, currentUser, communityMemberships, projectAcl, projects],
  );

  // Build the rendered worklist: incomplete (sorted by severity) +
  // optionally completed (when toggle is on).
  const incomplete = useMemo(
    () => listIncompleteMilestones(archetypeConfig, achievedMilestones),
    [archetypeConfig, achievedMilestones],
  );
  const completed = useMemo(
    () => listCompletedMilestones(archetypeConfig, achievedMilestones),
    [archetypeConfig, achievedMilestones],
  );

  /*
    Most milestones navigate to a route via the CTA. Two milestones
    are in-page rather than route-based:
      - accept_eula      → opens EulaModal
      - enable_2fa       → opens TotpSetupModal in place (architect
                            directive 2026-07-10 — one enrollment
                            experience at every JIT callsite)
    Both keep the user in context. Their respective components
    invalidate the security cache after success, so the milestone
    clears on the next render automatically.
  */
  const [totpOpen, setTotpOpen] = useState(false);
  const handleMilestoneCta = useCallback(
    (milestone: MilestoneUnlock) => {
      if (milestone.milestone === 'accept_eula') {
        setEulaOpen(true);
        return;
      }
      if (milestone.milestone === 'enable_2fa') {
        setTotpOpen(true);
        return;
      }
      if (milestone.ctaRoute)
        navigate(
          milestone.ctaRoute,
          milestone.ctaState ? { state: milestone.ctaState } : undefined,
        );
    },
    [navigate],
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.root}>
      {/* Header / view controls */}
      <div className={styles.viewControls}>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
          <span>{t('actionCenter.tasks.showCompleted')}</span>
        </label>
        <span className={styles.viewHint}>
          {t('actionCenter.tasks.archetypeHint', {
            archetype: t(archetypeConfig.labelKey),
          })}
        </span>
      </div>

      {/* Empty state — nothing outstanding */}
      {incomplete.length === 0 && (!showCompleted || completed.length === 0) && (
        <div className={styles.emptyState}>
          <span className={styles.emptyTitle}>
            {t('actionCenter.tasks.allDoneTitle')}
          </span>
          <span className={styles.emptyHint}>
            {t('actionCenter.tasks.allDoneHint')}
          </span>
        </div>
      )}

      {/* Outstanding milestones */}
      {incomplete.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('actionCenter.tasks.outstandingTitle')}
          </h2>
          <ul className={styles.taskList} role="list">
            {incomplete.map((m: MilestoneUnlock) => (
              <li
                key={m.milestone}
                className={`${styles.taskCard} ${styles[`severity_${m.severity}`]}`}
              >
                <div className={styles.taskMain}>
                  <div className={styles.taskHeader}>
                    <span
                      className={`${styles.severityPill} ${styles[`pill_${m.severity}`]}`}
                      aria-label={severityLabel(m.severity)}
                    >
                      {severityLabel(m.severity)}
                    </span>
                    <span className={styles.taskTitle}>
                      {t(`onboarding.milestones.${m.milestone}.label`)}
                    </span>
                  </div>
                  <p className={styles.taskNudge}>{t(m.nudgeKey)}</p>
                </div>
                {(m.ctaRoute
                  || m.milestone === 'accept_eula'
                  || m.milestone === 'enable_2fa') && (
                  <div className={styles.taskActions}>
                    <button
                      type="button"
                      className={styles.ctaBtn}
                      onClick={() => handleMilestoneCta(m)}
                    >
                      {m.ctaLabelKey
                        ? t(m.ctaLabelKey)
                        : t('actionCenter.tasks.openCta')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* EULA modal — opened by the accept_eula milestone CTA */}
      <EulaModal
        open={eulaOpen}
        onAccepted={() => setEulaOpen(false)}
        onAbort={() => setEulaOpen(false)}
      />

      {/* In-place 2FA enrollment — opened by the enable_2fa milestone CTA
          (2026-07-10; one experience with the Action Center tray). */}
      <TotpSetupModal open={totpOpen} onClose={() => setTotpOpen(false)} />

      {/* Completed milestones (toggle-gated) */}
      {showCompleted && completed.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('actionCenter.tasks.completedTitle')}
          </h2>
          <ul className={styles.taskList} role="list">
            {completed.map((m: MilestoneUnlock) => (
              <li key={m.milestone} className={`${styles.taskCard} ${styles.taskCardCompleted}`}>
                <div className={styles.taskMain}>
                  <div className={styles.taskHeader}>
                    <span className={styles.completedCheck} aria-hidden="true">✓</span>
                    <span className={styles.taskTitle}>
                      {t(`onboarding.milestones.${m.milestone}.label`)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default TasksTab;
